import type { ReactNode } from 'react'
import { BoardChartSvg } from './BoardReportCharts'
import {
  businessAreaRows,
  donutSegmentPath,
  governancePositionChartMode,
  governancePositionChartRows,
  niceMaximum,
  roadmapActivityPoints,
  strategicAlignmentRows,
  wrapBoardLabel,
  type BoardChartCode,
  type BoardChartRow,
} from './board-chart-model'
import { formatBoardDate, formatBoardDateTime, numberValue, pageDefinition, record, records, textValue, type ManagementStatement, type ReportPageCode, type ReportingPayload } from './reporting-model'

const palette = {
  ink: '#111111', muted: '#5d5a56', paper: '#f4f2ed', white: '#ffffff', line: '#d7d2ca',
  pink: '#e40046', pinkSoft: '#f9dce5', amber: '#c77900', green: '#1f6a4c', blue: '#285f89',
}

function truncate(value: unknown, length: number): string {
  const text = textValue(value)
  return text.length > length ? `${text.slice(0, Math.max(1, length - 1)).trim()}…` : text
}

function managementCommentaryLines(value: string): { lines: string[]; fontSize: number; lineHeight: number } {
  const dense = value.length > 420
  const veryDense = value.length > 620
  const maxChars = veryDense ? 70 : dense ? 62 : 52
  const lines = value.split(/\r?\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .flatMap((paragraph) => wrapBoardLabel(paragraph, maxChars, 100))
  return {
    lines,
    fontSize: veryDense ? 14 : dense ? 16 : 18,
    lineHeight: veryDense ? 17 : dense ? 20 : 24,
  }
}

function headlineFor(code: ReportPageCode, payload: ReportingPayload): string {
  const headlines = record(payload.headlines)
  const key = code === 'executive_overview' ? 'executiveOverview'
    : code === 'ai_portfolio_governance' ? 'aiPortfolioGovernance'
      : code === 'strategy_roadmap' ? 'strategyRoadmap'
        : code === 'risk_position' ? 'riskPosition' : 'governanceActionsChange'
  return textValue(headlines[key], pageDefinition(code).question)
}

function ReportFrame({ code, payload, snapshotNo, children }: { code: ReportPageCode; payload: ReportingPayload; snapshotNo?: number; children: ReactNode }) {
  const page = pageDefinition(code)
  const organisation = record(payload.organisation)
  const headline = headlineFor(code, payload)
  return <svg className="report-canvas-svg" data-report-page={code} viewBox="0 0 1920 1080" width="1920" height="1080" role="img" aria-labelledby={`${code}-title ${code}-description`} style={{ fontFamily: 'Arial, Helvetica, sans-serif', background: palette.paper }}>
    <title id={`${code}-title`}>{page.title}</title>
    <desc id={`${code}-description`}>{headline} Reporting position at {formatBoardDate(payload.asOf)}.</desc>
    <rect width="1920" height="1080" fill={palette.paper} />
    <rect x="0" y="0" width="1920" height="18" fill={palette.pink} />
    <text x="100" y="82" fontSize="26" fontWeight="700" fill={palette.ink} letterSpacing="2">SWELL AI</text>
    <text x="1818" y="82" textAnchor="end" fontSize="21" fill={palette.muted}>{truncate(organisation.name, 54)}</text>
    <text x="100" y="140" fontSize="19" fontWeight="700" fill={palette.pink} letterSpacing="2.5">{String(page.number).padStart(2, '0')} / {page.title.toUpperCase()}</text>
    <text x="100" y="202" fontSize="50" fontWeight="750" fill={palette.ink}>{page.title}</text>
    <text x="100" y="245" fontSize="23" fill={palette.muted}>{truncate(headline, 132)}</text>
    {children}
    <line x1="100" y1="1012" x2="1820" y2="1012" stroke={palette.line} strokeWidth="2" />
    <text x="100" y="1050" fontSize="18" fill={palette.muted}>Position at {formatBoardDateTime(payload.asOf)} · Missing information remains explicit</text>
    <text x="1820" y="1050" textAnchor="end" fontSize="18" fill={palette.muted}>{snapshotNo ? `Reporting Snapshot ${snapshotNo}` : 'Preview · not yet generated'}</text>
  </svg>
}

function SummaryMetric({ x, label, value, detail, tone = palette.blue }: { x: number; label: string; value: string | number; detail: string; tone?: string }) {
  return <g>
    <rect x={x} y="290" width="395" height="102" rx="16" fill={palette.white} />
    <rect x={x + 18} y="309" width="7" height="64" rx="3" fill={tone} />
    <text x={x + 43} y="326" fontSize="15" fontWeight="700" fill={palette.muted}>{label.toUpperCase()}</text>
    <text x={x + 43} y="369" fontSize="38" fontWeight="750" fill={palette.ink}>{value}</text>
    <text x={x + 118} y="362" fontSize="16" fill={palette.muted}>{truncate(detail, 34)}</text>
  </g>
}

function EmbeddedHorizontalChart({ code, x, y, width, height, title, subtitle, rows, series, labelWidth }: {
  code: BoardChartCode
  x: number
  y: number
  width: number
  height: number
  title: string
  subtitle: string
  rows: BoardChartRow[]
  series: Array<{ label: string; colour: string }>
  labelWidth: number
}) {
  const plotX = x + labelWidth
  const plotWidth = width - labelWidth - 70
  const chartTop = y + 118
  const chartHeight = height - 155
  const rowHeight = chartHeight / Math.max(rows.length, 1)
  const maximum = niceMaximum(rows.map((row) => Math.max(...row.values, 0)))
  const barHeight = Math.min(series.length > 1 ? 10 : 16, rowHeight / Math.max(series.length, 1) - 4)
  return <g data-board-chart={code} data-chart-treatment="horizontal-bars">
    <rect x={x} y={y} width={width} height={height} rx="18" fill={palette.white} />
    <text x={x + 28} y={y + 40} fontSize="24" fontWeight="750" fill={palette.ink}>{title}</text>
    <text x={x + 28} y={y + 69} fontSize="16" fill={palette.muted}>{subtitle}</text>
    <g aria-label="Chart legend">
      {series.map((item, index) => <g key={item.label} transform={`translate(${x + 28 + index * 176} ${y + 86})`}>
        <rect width="15" height="15" rx="3" fill={item.colour} />
        <text x="24" y="13" fontSize="15" fontWeight="700" fill={palette.ink}>{item.label}</text>
      </g>)}
    </g>
    {!rows.length ? <text x={x + 28} y={y + 150} fontSize="19" fill={palette.muted}>No information has been recorded.</text> : null}
    {rows.length ? <>
      <line x1={plotX} y1={chartTop - 8} x2={plotX} y2={chartTop + chartHeight} stroke={palette.line} />
      {rows.map((row, rowIndex) => {
        const centreY = chartTop + rowIndex * rowHeight + rowHeight / 2
        const lines = wrapBoardLabel(row.label, width > 1000 ? 54 : 28, 2)
        const lineHeight = 18
        const firstY = centreY - ((lines.length - 1) * lineHeight) / 2 + 6
        const groupHeight = series.length * barHeight + Math.max(0, series.length - 1) * 4
        return <g key={`${row.label}-${rowIndex}`}>
          <text x={x + 28} y={firstY} fontSize="17" fontWeight="650" fill={palette.ink}>
            {lines.map((line, index) => <tspan key={`${line}-${index}`} x={x + 28} dy={index ? lineHeight : 0}>{line}</tspan>)}
          </text>
          {row.values.map((value, index) => {
            const barY = centreY - groupHeight / 2 + index * (barHeight + 4)
            const barWidth = plotWidth * value / maximum
            return <g key={`${series[index]?.label ?? index}`}>
              <rect x={plotX} y={barY} width={plotWidth} height={barHeight} rx={barHeight / 2} fill="#ece9e4" />
              <rect x={plotX} y={barY} width={barWidth} height={barHeight} rx={barHeight / 2} fill={series[index]?.colour ?? palette.blue} />
              <text x={Math.min(plotX + barWidth + 10, x + width - 28)} y={barY + barHeight} fontSize="16" fontWeight="750" fill={palette.ink}>{value}</text>
            </g>
          })}
        </g>
      })}
    </> : null}
  </g>
}

function EmbeddedGovernancePositionChart({ payload, x, y, width, height }: { payload: ReportingPayload; x: number; y: number; width: number; height: number }) {
  const rows = governancePositionChartRows(payload)
  const mode = governancePositionChartMode(rows)
  if (mode === 'horizontal-bars') return <EmbeddedHorizontalChart code="governance_position" x={x} y={y} width={width} height={height} title="Governance position" subtitle="Current AI uses by recorded decision position." rows={rows} series={[{ label: 'AI Uses', colour: palette.green }]} labelWidth={300} />

  const total = rows.reduce((sum, row) => sum + numberValue(row.values[0]), 0)
  const centreX = x + 235
  const centreY = y + 315
  const outerRadius = 143
  const innerRadius = 89
  const colours = [palette.green, palette.pink, palette.amber, palette.blue]
  let angle = -90

  return <g data-board-chart="governance_position" data-chart-treatment={mode}>
    <rect x={x} y={y} width={width} height={height} rx="18" fill={palette.white} />
    <text x={x + 28} y={y + 40} fontSize="24" fontWeight="750" fill={palette.ink}>Governance position</text>
    <text x={x + 28} y={y + 69} fontSize="16" fill={palette.muted}>Current AI uses by recorded decision position.</text>
    {mode === 'empty' ? <text x={x + 28} y={y + 150} fontSize="19" fill={palette.muted}>No current AI Uses have a recorded governance position.</text> : null}
    {mode === 'donut' ? <>
      {rows.map((row, index) => {
        const value = numberValue(row.values[0])
        const percentage = total ? Math.round(value / total * 100) : 0
        const startAngle = angle
        const endAngle = angle + (total ? value / total * 360 : 0)
        angle = endAngle
        return <path key={`${row.label}-${index}`} d={donutSegmentPath(centreX, centreY, outerRadius, innerRadius, startAngle, endAngle)} fill={colours[index] ?? palette.muted} data-donut-segment={row.label} aria-label={`${row.label}: ${value}, ${percentage}%`} />
      })}
      <text x={centreX} y={centreY - 3} textAnchor="middle" fontSize="46" fontWeight="750" fill={palette.ink}>{total}</text>
      <text x={centreX} y={centreY + 28} textAnchor="middle" fontSize="15" fontWeight="700" fill={palette.muted}>Current AI Uses</text>
      <g aria-label="Governance position legend">
        {rows.map((row, index) => {
          const value = numberValue(row.values[0])
          const percentage = total ? Math.round(value / total * 100) : 0
          const legendY = y + 145 + index * 92
          const lines = wrapBoardLabel(row.label, 32, 2)
          return <g key={`${row.label}-legend`} transform={`translate(${x + 445} ${legendY})`}>
            <rect width="20" height="20" rx="4" fill={colours[index] ?? palette.muted} />
            <text x="32" y="16" fontSize="17" fontWeight="700" fill={palette.ink}>
              {lines.map((line, lineIndex) => <tspan key={`${line}-${lineIndex}`} x="32" dy={lineIndex ? 19 : 0}>{line}</tspan>)}
            </text>
            <text x="32" y={40 + Math.max(0, lines.length - 1) * 19} fontSize="16" fill={palette.muted}>{value}{' \u00b7 '}{percentage}%</text>
          </g>
        })}
      </g>
    </> : null}
  </g>
}

function EmbeddedRoadmapActivity({ payload, x, y, width, height }: { payload: ReportingPayload; x: number; y: number; width: number; height: number }) {
  const points = roadmapActivityPoints(payload)
  const plotX = x + 76
  const plotY = y + 94
  const plotWidth = width - 116
  const plotHeight = height - 142
  const maximum = niceMaximum(points.map((point) => point.total))
  const slot = plotWidth / Math.max(points.length, 1)
  const barWidth = Math.min(68, slot * .52)
  return <g data-board-chart="roadmap_activity">
    <rect x={x} y={y} width={width} height={height} rx="18" fill={palette.white} />
    <text x={x + 28} y={y + 39} fontSize="24" fontWeight="750" fill={palette.ink}>Initiative activity across the roadmap</text>
    <text x={x + 28} y={y + 67} fontSize="16" fill={palette.muted}>Initiatives starting and continuing in each quarter.</text>
    <g aria-label="Chart legend">
      <rect x={x + width - 332} y={y + 28} width="15" height="15" rx="3" fill={palette.pink} /><text x={x + width - 309} y={y + 41} fontSize="15" fontWeight="700" fill={palette.ink}>Starting</text>
      <rect x={x + width - 190} y={y + 28} width="15" height="15" rx="3" fill={palette.blue} /><text x={x + width - 167} y={y + 41} fontSize="15" fontWeight="700" fill={palette.ink}>Continuing</text>
    </g>
    {!points.length ? <text x={x + 28} y={y + 130} fontSize="19" fill={palette.muted}>Initiative timing has not been recorded.</text> : null}
    {points.length ? <>
      <line x1={plotX} y1={plotY + plotHeight} x2={plotX + plotWidth} y2={plotY + plotHeight} stroke={palette.line} />
      {points.map((point, index) => {
        const barX = plotX + slot * index + (slot - barWidth) / 2
        const startingHeight = plotHeight * point.starting / maximum
        const continuingHeight = plotHeight * point.continuing / maximum
        const base = plotY + plotHeight
        return <g key={`${point.quarterStart}-${index}`}>
          <rect x={barX} y={base - continuingHeight} width={barWidth} height={continuingHeight} rx="4" fill={palette.blue} />
          <rect x={barX} y={base - continuingHeight - startingHeight} width={barWidth} height={startingHeight} rx="4" fill={palette.pink} />
          <text x={barX + barWidth / 2} y={base - continuingHeight - startingHeight - 7} textAnchor="middle" fontSize="15" fontWeight="750" fill={palette.ink}>{point.total}</text>
          <text x={barX + barWidth / 2} y={base + 24} textAnchor="middle" fontSize="14" fill={palette.muted}>{point.label}</text>
        </g>
      })}
    </> : null}
  </g>
}

function TwoChartPage({ payload, left, right }: { payload: ReportingPayload; left: Parameters<typeof BoardChartSvg>[0]['code']; right: Parameters<typeof BoardChartSvg>[0]['code'] }) {
  return <>
    <BoardChartSvg code={left} payload={payload} idPrefix={`page-${left}`} x={100} y={290} width={830} height={667} />
    <BoardChartSvg code={right} payload={payload} idPrefix={`page-${right}`} x={990} y={290} width={830} height={667} />
  </>
}

function ExecutiveOverview({ payload, statement }: { payload: ReportingPayload; statement: ManagementStatement | null }) {
  const portfolio = record(payload.portfolio)
  const governance = record(payload.governance)
  const positions = record(portfolio.positions)
  const growth = record(record(payload.strategy).roadmapGrowth)
  const metrics = [
    ['AI Initiatives', numberValue(growth.totalInitiatives), 'roadmap initiatives'],
    ['AI Uses', numberValue(portfolio.totalUses), 'current records'],
    ['Governance action', numberValue(positions.needsAttention), 'current AI Uses'],
    ['Decisions awaiting action', numberValue(governance.awaitingDecisions), 'approval requests'],
  ]
  const commentary = statement ? managementCommentaryLines(statement.text) : null
  const attribution = statement?.authorUserId && statement.recordedAt
    ? `Recorded by ${statement.authorName} · ${formatBoardDateTime(statement.recordedAt)}`
    : 'Author and time will be recorded when the report is created'
  return <>
    <BoardChartSvg code="initiative_maturity" payload={payload} idPrefix="page-executive" x={100} y={290} width={1120} height={667} />
    <rect x="1250" y="290" width="570" height="667" rx="20" fill={palette.white} />
    <text x="1290" y="345" fontSize="22" fontWeight="700" fill={palette.ink}>Current portfolio position</text>
    {metrics.map(([label, value, detail], index) => {
      const compactX = 1290 + (index % 2) * 250
      const compactY = 392 + Math.floor(index / 2) * 112
      return <g key={String(label)}>
        <text x={statement ? compactX : 1290} y={statement ? compactY : 398 + index * 118} fontSize={statement ? 14 : 16} fontWeight="700" fill={palette.muted}>{String(label).toUpperCase()}</text>
        <text x={statement ? compactX : 1290} y={statement ? compactY + 39 : 439 + index * 118} fontSize={statement ? 34 : 38} fontWeight="750" fill={palette.ink}>{value}</text>
        <text x={statement ? compactX : 1370} y={statement ? compactY + 62 : 431 + index * 118} fontSize={statement ? 14 : 16} fill={palette.muted}>{String(detail)}</text>
      </g>
    })}
    {statement ? <>
      <line x1="1290" y1="584" x2="1780" y2="584" stroke={palette.line} />
      <text x="1290" y="624" fontSize="18" fontWeight="700" fill={palette.pink}>MANAGEMENT COMMENTARY</text>
      <text x="1290" y="660" fontSize={commentary?.fontSize ?? 18} fill={palette.ink}>
        {commentary?.lines.map((line, index) => <tspan key={`${line}-${index}`} x="1290" dy={index === 0 ? 0 : commentary.lineHeight}>{line}</tspan>)}
      </text>
      <text x="1290" y="925" fontSize="14" fill={palette.muted}>{attribution}</text>
    </> : null}
  </>
}

function PortfolioGovernance({ payload }: { payload: ReportingPayload }) {
  const portfolio = record(payload.portfolio)
  const positions = record(portfolio.positions)
  return <>
    <SummaryMetric x={100} label="Current AI Uses" value={numberValue(portfolio.totalUses)} detail={`${numberValue(portfolio.liveOrPilot)} live or pilot`} />
    <SummaryMetric x={512} label="Needs attention" value={numberValue(positions.needsAttention)} detail="current records" tone={palette.pink} />
    <SummaryMetric x={924} label="Governance underway" value={numberValue(positions.governanceInProgress)} detail="current records" tone={palette.amber} />
    <SummaryMetric x={1336} label="Missing owners" value={numberValue(portfolio.missingOwners)} detail="current AI Uses" tone={palette.amber} />
    <EmbeddedHorizontalChart code="business_area" x={100} y={414} width={830} height={540} title="AI uses by business area" subtitle="Current recorded AI uses, ordered by business area." rows={businessAreaRows(payload)} series={[{ label: 'AI Uses', colour: palette.blue }]} labelWidth={300} />
    <EmbeddedGovernancePositionChart payload={payload} x={990} y={414} width={830} height={540} />
  </>
}

function StrategyRoadmap({ payload }: { payload: ReportingPayload }) {
  return <>
    <EmbeddedHorizontalChart code="strategic_alignment" x={100} y={290} width={1720} height={405} title="AI alignment by strategic priority" subtitle="AI Initiatives and AI Uses linked to each strategic priority." rows={strategicAlignmentRows(payload)} series={[{ label: 'AI Initiatives', colour: palette.green }, { label: 'AI Uses', colour: palette.pink }]} labelWidth={570} />
    <EmbeddedRoadmapActivity payload={payload} x={100} y={720} width={1720} height={237} />
  </>
}

function RiskPosition({ payload }: { payload: ReportingPayload }) {
  const matrix = record(record(payload.risk).matrix)
  const categories = records(record(payload.risk).categories)
  const outside = categories.reduce((sum, category) => sum + numberValue(category.outsideTolerance), 0)
  return <>
    <SummaryMetric x={100} label="Current AI risks" value={numberValue(matrix.totalCurrent)} detail="recorded risks" />
    <SummaryMetric x={512} label="Rated" value={numberValue(matrix.ratedRiskCount)} detail="residual position" tone={palette.green} />
    <SummaryMetric x={924} label="Outside tolerance" value={outside} detail="current risks" tone={palette.pink} />
    <SummaryMetric x={1336} label="Not yet rated" value={numberValue(matrix.notRatedCount)} detail="current risks" tone={palette.amber} />
    <BoardChartSvg code="risk_matrix" payload={payload} idPrefix="page-risk-matrix" x={100} y={414} width={830} height={540} />
    <BoardChartSvg code="risk_categories" payload={payload} idPrefix="page-risk-categories" x={990} y={414} width={830} height={540} />
  </>
}

function GovernanceActionsChange({ payload }: { payload: ReportingPayload }) {
  return <TwoChartPage payload={payload} left="governance_workload" right="material_changes" />
}

export function ReportCanvas({ code, payload, snapshotNo, managementStatement = null }: {
  code: ReportPageCode
  payload: ReportingPayload
  snapshotNo?: number
  managementStatement?: ManagementStatement | null
}) {
  return <ReportFrame code={code} payload={payload} snapshotNo={snapshotNo}>
    {code === 'executive_overview' ? <ExecutiveOverview payload={payload} statement={managementStatement} /> : null}
    {code === 'ai_portfolio_governance' ? <PortfolioGovernance payload={payload} /> : null}
    {code === 'strategy_roadmap' ? <StrategyRoadmap payload={payload} /> : null}
    {code === 'risk_position' ? <RiskPosition payload={payload} /> : null}
    {code === 'governance_actions_change' ? <GovernanceActionsChange payload={payload} /> : null}
  </ReportFrame>
}
